import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { GlassButton } from "src/components/GlassButtons";
import { ADModalBodyNoScroll, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import {
   listOverlayImages,
   OverlayListingItem,
   uploadOverlayImage,
   saveOverlayRecordToEs,
   downloadOverlayImage,
   deriveOverlayPlacement,
   computeDefaultOverlayCoordinates,
   deleteOverlayImage,
   buildFileFromDataUrl,
   blobToDataUrl,
   sanitizeFilename,
} from "src/modules/ANNModule/ANNImageOverlayStorage";
import { LayerFunc } from "src/managers/LayerFunc";
import { LayerInfo, LayerType } from "src/common/managers/Types";
import { MapitStateContext, actionUpdateDataLayer } from "src/states/MapitState";
import {
   AppMessagesContext,
   actionSetErrorMessage,
} from "@viamap/viamap2-common";
import maplibregl from "maplibre-gl";
import { closeWindow, ManagedWindow, openWindow, WindowId } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
type PanelProps = {
   onReload: () => void;
   onUpload: () => void;
   onLoadOverlay: (file: OverlayListingItem) => void;
   onDeleteOverlay: (file: OverlayListingItem) => void;
   overlayFiles: OverlayListingItem[];
   isLoadingList: boolean;
   isUploading: boolean;
   hasLoaded: boolean;
   loadingKey: string | null;
   deletingKey: string | null;
   overlayLayerOptions: { id: string; label: string }[];
   selectedLayerId: string | null;
   onSelectLayer: (layerId: string) => void;
};
function ANNImageOverlayDebugPanel({
   onReload,
   onUpload,
   onLoadOverlay,
   onDeleteOverlay,
   overlayFiles,
   isLoadingList,
   isUploading,
   hasLoaded,
   loadingKey,
   deletingKey,
   overlayLayerOptions,
   selectedLayerId,
   onSelectLayer,
}: PanelProps) {
   return (
      <ADModalBodyNoScroll templateRows="auto 1fr">
         <div className="image-overlay-debug-panel__actions">
            <div className="image-overlay-debug-panel__select-group">
               <label htmlFor="image-overlay-layer-select">Select overlay layer</label>
               <select
                  id="image-overlay-layer-select"
                  className="image-overlay-debug-panel__select"
                  value={selectedLayerId ?? ""}
                  onChange={(event) => onSelectLayer(event.target.value)}
                  disabled={overlayLayerOptions.length === 0}
               >
                  {overlayLayerOptions.length === 0 && <option value="">No overlay layers found</option>}
                  {overlayLayerOptions.map((option) => (
                     <option key={option.id} value={option.id}>
                        {option.label}
                     </option>
                  ))}
               </select>
            </div>
            <GlassButton onClick={onUpload} disabled={isUploading || !selectedLayerId}>
               {isUploading ? "Uploading overlay..." : "Upload selected overlay"}
            </GlassButton>
            <GlassButton onClick={onReload} disabled={isLoadingList}>
               {isLoadingList ? "Loading overlays..." : "Reload overlays"}
            </GlassButton>
         </div>
         <div className="image-overlay-debug-list image-overlay-debug-panel__list">
            {hasLoaded && overlayFiles.length === 0 && !isLoadingList && (
               <span className="image-overlay-debug-list__empty">No overlay images found</span>
            )}
            {overlayFiles.map((file) => (
               <div key={file.key} className="image-overlay-debug-list__item">
                  <span className="image-overlay-debug-list__label">{file.key.split("/").pop() || file.key}</span>
                  <button
                     className="image-overlay-debug-list__button"
                     disabled={loadingKey === file.key}
                     onClick={() => onLoadOverlay(file)}
                  >
                     {loadingKey === file.key ? "Loading..." : "Load"}
                  </button>
                  <button
                     className="image-overlay-debug-list__button image-overlay-debug-list__button--danger"
                     disabled={deletingKey === file.key}
                     onClick={() => onDeleteOverlay(file)}
                  >
                     {deletingKey === file.key ? "Deleting..." : "Delete"}
                  </button>
               </div>
            ))}
         </div>
      </ADModalBodyNoScroll>
   );
}
export function ANNImageOverlayDebugWindow({ window: managedWindow }: { window: ManagedWindow }) {
   const { dispatch: windowDispatch } = useWindowContext();
   const { state: mapitState, dispatch: mapitStateDispatch } = useContext(MapitStateContext);
   const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);
   const [overlayFiles, setOverlayFiles] = useState<OverlayListingItem[]>([]);
   const [isLoadingOverlayList, setIsLoadingOverlayList] = useState(false);
   const [hasLoadedOverlayList, setHasLoadedOverlayList] = useState(false);
   const [isUploadingOverlay, setIsUploadingOverlay] = useState(false);
   const [loadingOverlayKey, setLoadingOverlayKey] = useState<string | null>(null);
   const [deletingOverlayKey, setDeletingOverlayKey] = useState<string | null>(null);
   const overlayLayers = useMemo(
      () =>
         Object.values(mapitState.layers || {}).filter(
            (layer): layer is LayerInfo => layer.type === LayerType.ImageOverlay && Boolean(layer.imageOverlay)
         ),
      [mapitState.layers]
   );
   const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
   useEffect(() => {
      if (overlayLayers.length === 0) {
         setSelectedLayerId(null);
         return;
      }
      if (!selectedLayerId || !overlayLayers.some((layer) => String(layer.layerId) === selectedLayerId)) {
         setSelectedLayerId(String(overlayLayers[0].layerId));
      }
   }, [overlayLayers, selectedLayerId]);
   const loadOverlayList = useCallback(async () => {
      setIsLoadingOverlayList(true);
      try {
         const items = await listOverlayImages();
         setOverlayFiles(items);
         setHasLoadedOverlayList(true);
      } catch (error: any) {
         appMessageDispatch(actionSetErrorMessage(error?.message || "Failed to load overlay images"));
         setHasLoadedOverlayList(true);
      } finally {
         setIsLoadingOverlayList(false);
      }
   }, [appMessageDispatch]);
   useEffect(() => {
      if (!hasLoadedOverlayList && !isLoadingOverlayList) {
         loadOverlayList();
      }
   }, [hasLoadedOverlayList, isLoadingOverlayList, loadOverlayList]);
   const simulateOverlayFromEs = useCallback(
      (options?: {
         dataUrl?: string;
         layerName?: string;
         fileName?: string;
         opacity?: number;
         coordinates?: [number, number][];
         aspectRatio?: number;
      }) => {
         const mapInstance = mapitState.map?.getMapPop?.() as maplibregl.Map | undefined;
         const coordinates =
            options?.coordinates ||
            computeDefaultOverlayCoordinates(mapInstance, options?.aspectRatio ?? 1);
         const layerName = options?.layerName || "Historic Map Overlay";
         const fileName = options?.fileName || "historic-overlay.png";
         const dataUrl =
            options?.dataUrl ||
            "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9d/Brian_Shaw_Arnold_Classic_2017e.jpg/500px-Brian_Shaw_Arnold_Classic_2017e.jpg";
         const newLayer = LayerFunc.createLayerInfoForImageOverlay(layerName, fileName, dataUrl, coordinates);
         windowDispatch(openWindow(WindowId.ImageOverlayStandalone, { layerInfo: newLayer }));
      },
      [mapitState.map, mapitStateDispatch]
   );
   const uploadSelectedOverlayLayer = useCallback(async () => {
      const overlayLayer = overlayLayers.find((layer) => String(layer.layerId) === selectedLayerId);
      if (!overlayLayer || !overlayLayer.imageOverlay) {
         return;
      }
      const dataUrl = overlayLayer.imageOverlay.dataUrl;
      if (!dataUrl) {
         appMessageDispatch(actionSetErrorMessage("Selected overlay has no image data."));
         return;
      }
      setIsUploadingOverlay(true);
      try {
         const file = await buildFileFromDataUrl(
            dataUrl,
            sanitizeFilename(overlayLayer.datasetname || overlayLayer.filename || `overlay-${overlayLayer.layerId}`)
         );
         const { key, url } = await uploadOverlayImage(file);
         await saveOverlayRecordToEs({
            s3Key: key,
            url,
            coordinates: overlayLayer.imageOverlay.coordinates,
            opacity: overlayLayer.styling.opacity ?? 1,
            fileName: file.name,
         });
         const updatedLayer: LayerInfo = {
            ...overlayLayer,
            imageOverlay: {
               ...overlayLayer.imageOverlay,
               dataUrl: url,
            },
         };
         mapitStateDispatch(actionUpdateDataLayer(overlayLayer.layerId, updatedLayer));
      } catch (error: any) {
         appMessageDispatch(actionSetErrorMessage(error?.message || "Failed to upload overlay."));
      } finally {
         setIsUploadingOverlay(false);
      }
   }, [appMessageDispatch, mapitStateDispatch, overlayLayers, selectedLayerId]);
   const handleLoadOverlayFromS3 = useCallback(
      async (file: OverlayListingItem) => {
         setLoadingOverlayKey(file.key);
         try {
            const { blob } = await downloadOverlayImage(file.key);
            const mapInstance = mapitState.map?.getMapPop?.() as maplibregl.Map | undefined;
            const { aspectRatio, coordinates } = await deriveOverlayPlacement({ blob }, mapInstance);
            const dataUrl = await blobToDataUrl(blob);
            simulateOverlayFromEs({
               dataUrl,
               layerName: file.key.split("/").pop() || file.key,
               fileName: file.key,
               coordinates,
               aspectRatio,
            });
         } catch (error: any) {
            appMessageDispatch(actionSetErrorMessage(error?.message || "Failed to load overlay image."));
         } finally {
            setLoadingOverlayKey(null);
         }
      },
      [appMessageDispatch, mapitState.map, simulateOverlayFromEs]
   );
   const handleDeleteOverlayFromS3 = useCallback(
      async (file: OverlayListingItem) => {
         if (!window.confirm(`Delete overlay '${file.key}'?`)) {
            return;
         }
         setDeletingOverlayKey(file.key);
         try {
            await deleteOverlayImage(file.key);
            setOverlayFiles((prev) => prev.filter((item) => item.key !== file.key));
         } catch (error: any) {
            appMessageDispatch(actionSetErrorMessage(error?.message || "Failed to delete overlay image."));
         } finally {
            setDeletingOverlayKey(null);
         }
      },
      [appMessageDispatch]
   );
   return (
      <AdvancedDragModal
         window={managedWindow}
         PosDefault={{ left: "60%", top: "50%", width: "420px", height: "400px", transform: "translate(-50%, -50%)" }}
         title="Image overlays"
         saveKey="ImageOverlayDebugPanel"
         allowUserMiniMize
         onClose={() => windowDispatch(closeWindow(managedWindow.id))}
      >
         <ANNImageOverlayDebugPanel
            onReload={loadOverlayList}
            onUpload={uploadSelectedOverlayLayer}
            onLoadOverlay={handleLoadOverlayFromS3}
            onDeleteOverlay={handleDeleteOverlayFromS3}
            overlayFiles={overlayFiles}
            isLoadingList={isLoadingOverlayList}
            isUploading={isUploadingOverlay}
            hasLoaded={hasLoadedOverlayList}
            loadingKey={loadingOverlayKey}
            deletingKey={deletingOverlayKey}
            overlayLayerOptions={overlayLayers.map((layer) => ({
               id: String(layer.layerId),
               label: layer.datasetname || layer.filename || `Overlay ${layer.layerId}`,
            }))}
            selectedLayerId={selectedLayerId}
            onSelectLayer={(layerId) => setSelectedLayerId(layerId || null)}
         />
      </AdvancedDragModal>
   );
}
